import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { Clock, Users } from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import {
  formatListCardAbsoluteDate,
  formatListCardRelativeDate,
  LIST_CARD_RELATIVE_TIME_CLASS,
  listCardTimestampIso,
} from "./listCardTimestamps";

import { AvatarWithInitials, AVATAR_SIZE_NORMAL } from "../AvatarWithInitials";

type ListCardGridFooterProps = {
  idPrefix: string;
  entityId: string;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
  members: AskPunttChatUserSummary[];
  memberLabel: (member: AskPunttChatUserSummary) => string;
  /** Fade footer metadata when the parent card `group` is hovered (owned chat delete UX). */
  hideOnGroupHover?: boolean;
};

export function ListCardGridFooter({
  idPrefix,
  entityId,
  updatedAt,
  createdAt,
  members,
  memberLabel,
  hideOnGroupHover = false,
}: ListCardGridFooterProps) {
  const iso = listCardTimestampIso(updatedAt, createdAt);
  const absolute = formatListCardAbsoluteDate(iso);
  const relative = formatListCardRelativeDate(iso);
  const count = members.length;
  const visible = members.slice(0, 3);

  return (
    <div
      className={cx(
        "flex w-full items-center justify-end gap-3",
        hideOnGroupHover &&
          "transition-opacity duration-300 group-hover:pointer-events-none group-hover:opacity-0",
      )}
      data-testid={`${idPrefix}-${entityId}-footer`}
    >
      {absolute || relative ? (
        <div
          className="flex shrink-0 flex-col items-end gap-0 text-right"
          data-testid={`${idPrefix}-${entityId}-updated`}
        >
          {absolute ? (
            <span className="flex items-center justify-end gap-1 leading-5 text-puntt-neutral-gray-9">
              <Clock size={14} aria-hidden="true" />
              <Text size="1">{absolute}</Text>
            </span>
          ) : null}
          {relative ? (
            <span
              className={LIST_CARD_RELATIVE_TIME_CLASS}
              data-testid={`${idPrefix}-${entityId}-updated-relative`}
            >
              {relative}
            </span>
          ) : null}
        </div>
      ) : null}
      <ListCardMembersStack
        idPrefix={idPrefix}
        entityId={entityId}
        memberLabel={memberLabel}
        count={count}
        visible={visible}
      />
    </div>
  );
}

type ListCardRowTimestampsProps = {
  idPrefix: string;
  entityId: string;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
};

export function ListCardRowTimestamps({
  idPrefix,
  entityId,
  updatedAt,
  createdAt,
}: ListCardRowTimestampsProps) {
  const iso = listCardTimestampIso(updatedAt, createdAt);
  const absolute = formatListCardAbsoluteDate(iso);
  const relative = formatListCardRelativeDate(iso);
  if (!absolute && !relative) return null;

  return (
    <div
      className="flex shrink-0 flex-col items-end gap-0.5 text-right"
      data-testid={`${idPrefix}-${entityId}-updated`}
    >
      {absolute ? (
        <Text size="1" className="text-puntt-neutral-gray-9">
          {absolute}
        </Text>
      ) : null}
      {relative ? (
        <span
          className={LIST_CARD_RELATIVE_TIME_CLASS}
          data-testid={`${idPrefix}-${entityId}-updated-relative`}
        >
          {relative}
        </span>
      ) : null}
    </div>
  );
}

function ListCardMembersStack({
  idPrefix,
  entityId,
  memberLabel,
  count,
  visible,
}: {
  idPrefix: string;
  entityId: string;
  memberLabel: (member: AskPunttChatUserSummary) => string;
  count: number;
  visible: AskPunttChatUserSummary[];
}) {
  const { t } = useTranslation("ui");

  if (count === 0) {
    return (
      <span
        className="flex shrink-0 items-center justify-end gap-1 text-puntt-neutral-gray-9"
        data-testid={`${idPrefix}-${entityId}-members`}
        aria-label={t("listCardGridFooter.participantsCount", { count: 0 })}
      >
        <Users size={14} aria-hidden="true" />
        <Text size="1">0</Text>
      </span>
    );
  }

  return (
    <div
      className="flex shrink-0 items-center justify-end gap-1"
      data-testid={`${idPrefix}-${entityId}-members`}
      aria-label={t("listCardGridFooter.participantsCount", { count })}
    >
      {count > 3 ? (
        <Text size="1" className="text-puntt-neutral-gray-9">
          +{count - 3}
        </Text>
      ) : null}
      <Flex gap="0" align="center" justify="end" className="-space-x-1">
        {visible.map((m) => (
          <AvatarWithInitials
            key={m._id}
            size={AVATAR_SIZE_NORMAL}
            name={memberLabel(m)}
            avatar={m.avatar ?? undefined}
            colorKey={m._id}
            backgroundColor={m.avatarBackgroundColor ?? undefined}
            textColor={m.avatarTextColor ?? undefined}
            className="ring-2 ring-base-white"
            aria-hidden="true"
          />
        ))}
      </Flex>
    </div>
  );
}

export function ListCardRowMembers({
  idPrefix,
  entityId,
  members,
  memberLabel,
}: {
  idPrefix: string;
  entityId: string;
  members: AskPunttChatUserSummary[];
  memberLabel: (member: AskPunttChatUserSummary) => string;
}) {
  const count = members.length;
  const visible = members.slice(0, 3);
  return (
    <ListCardMembersStack
      idPrefix={idPrefix}
      entityId={entityId}
      memberLabel={memberLabel}
      count={count}
      visible={visible}
    />
  );
}
